import FirebaseHandlerBase from "../firebase_handler_base";
import Event from "../../../../utils/event";
import {SYSTEM_EVENTS} from "../../../../events/events";
import _ from "../../../../utils/underscore";
import {
  IInitGeoData,
  IKickData,
  IMessageData,
  IMessagesParams, IntrusiveMessageData,
  IOMGReceivedData,
  IRestrictionData,
  ISubscriptionParams,
  IWheelOfFortuneData,
  MESSAGE_ANIMATION_TYPE,
  MESSAGE_DISPLAY_MODE,
  MESSAGES_FEATURES_DEFAULTS,
  PLATFORM_MESSAGE_TYPE
} from "../firebase_const";
import {Check} from "@sparkware/app-utils";
import {
  IGamblingSessionActionData, IGamblingSessionActionDataReceivedFromPlatform, IGamblingSessionActionEvent,
  IMessageDataJson,
  IOMGParsedData,
  IReceivedMessage,
  MESSAGE_TYPE,
  OpenWithinGameType
} from "./messages_handler_interfaces";



export default class MessagesHandler extends FirebaseHandlerBase{
  static CLASS = "MessagesHandler";
  private _messagesConfig: IMessagesParams;

  protected setFeatureParams(messagesParams: IMessagesParams){
    super.setFeatureParams(messagesParams);
    this._messagesConfig = _.mergeDeep(MESSAGES_FEATURES_DEFAULTS, messagesParams);
  }

  protected getSubscriptionParams(): ISubscriptionParams {
    return {
      eventName: 'messages',
      successCallback: this._updateMessages,
      params: {omgVersion:this._messagesConfig.OMG.OMGVersion, toasterVersion:this._messagesConfig.Toasters.toasterVersion}
    };
  }

  protected getFeatureName(): string {
    return "Messages";
  }

  protected getFeatureLabel(): string {
    return "OMG/Toaster/Kick/Restrictions/BetRejected";
  }

  protected getFallbackServiceName(): string{
    return null;
  }

  private _updateMessages = (messages: IReceivedMessage[]) => {
    this.logger.debug('Firebase got OMG/Toaster/Kick/Restrictions/BetRejected messages: ' + JSON.stringify(messages));

    if (messages.length === 0) {
      return;
    }

    const restrictions: IRestrictionData[] = [];

    let category = null;
    messages.forEach((message: IReceivedMessage) => {
      const data: IMessageData = message.data;

      if(message.type !== MESSAGE_TYPE.ADDED){
        return; // only messages with type = ADDED are implemented
      }

      category = PLATFORM_MESSAGE_TYPE[data.PlatformMessagesType];
      if(category) {
        this.logger.debug(`Firebase ${category} message: ${JSON.stringify(data)}`);
      }

      switch(data.PlatformMessagesType) {

        case PLATFORM_MESSAGE_TYPE.OMG:
          if(this._messagesConfig.OMG.enabled) {
            const omgData: IOMGParsedData = this.parseOmgMessage(data as IOMGReceivedData);
            this.OMGFieldsValueChange(omgData);
            Event.fire(SYSTEM_EVENTS.SESSION_EVENT_OMG_RECEIVED, omgData);
          } else {
            this.logger.debug('Firebase OMG feature is disabled');
          }
          break;

        case PLATFORM_MESSAGE_TYPE.TOASTER:
          if (this._messagesConfig.Toasters.enabled) {
            const toasterData: IOMGReceivedData = data as IOMGReceivedData;
            if (toasterData.MessageDataJson) {
              this._handleToasterTypeMessages(JSON.parse(toasterData.MessageDataJson));
            } else {
              this.logger.debug('Firebase TOASTER feature is disabled');
            }
          }
          break;

        case PLATFORM_MESSAGE_TYPE.KICK:
          if(this._messagesConfig.Kick.enabled) {
            const kickData: IKickData = data as IKickData;
            Event.fire(SYSTEM_EVENTS.SESSION_EVENT_KICK, {
              KickType: kickData.KickType.toString(),
              KickReasonId: kickData.KickReasonId.toString(),
              LimitExpirationDateInSeconds: kickData.LimitExpirationDate?.seconds, //Only applicable for ukgc soft kick right now
              CorrelationID: kickData.CorrelationID || kickData.CorrelationId
            });
          } else {
            this.logger.debug('Firebase KICK feature is disabled');
          }
          break;
        case PLATFORM_MESSAGE_TYPE.INIT_GEO:
          const initGeoDataData: IInitGeoData = data as IInitGeoData;
          Event.fire(SYSTEM_EVENTS.GEO_PERIODIC_UPDATE, {
            mobileNumber: initGeoDataData.Mobile,
            licenseKey: initGeoDataData.GeoComplyLicense,
            licenseWebKey: initGeoDataData.GeoComplyWebLicense,
            locationTransactionId: initGeoDataData.LocationTransactionId
          });
          break;
        case PLATFORM_MESSAGE_TYPE.UPDATE_RESTRICTION_V2:
          if(this._messagesConfig.Restrictions.enabled) {
            const restrictionData: IRestrictionData = data as IRestrictionData;
            restrictions.push(restrictionData as IRestrictionData);
            if(restrictions.length > 0){
              this._updateRestrictions(data, restrictions);
            }
          } else {
            this.logger.debug('Firebase RESTRICTIONS feature is disabled');
          }
          break;
        case PLATFORM_MESSAGE_TYPE.BET_REJECTED:
          if(this._messagesConfig.BetRejected.enabled) {
            Event.fire(SYSTEM_EVENTS.BONUS_ABUSE_PARKING_MONEY, data);
          } else {
            this.logger.debug('Firebase BetRejected feature is disabled');
          }
          break;
        case PLATFORM_MESSAGE_TYPE.PROMOTION_DISPLAY_IN_CLIENT:
          Event.fire(SYSTEM_EVENTS.PROMOTIONS_CHANGED, data as IWheelOfFortuneData);
          break;
        case PLATFORM_MESSAGE_TYPE.INTRUSIVE_MESSAGE:
          const toasterData: IOMGReceivedData = data as IOMGReceivedData;
          if (toasterData.MessageDataJson) {
            this._handleIntrusiveMessages(JSON.parse(toasterData.MessageDataJson));
          }
          break;

        case PLATFORM_MESSAGE_TYPE.GAMBLING_SESSION_ACTION:

          const gamblingSessionActionEvent = data as IGamblingSessionActionEvent;
          if(!gamblingSessionActionEvent.MessageDataJson) {
            this.logger.error('Received gambling session action event with empty MessageDataJson');
            return;
          }

          let actionsReceivedFromPlatform: IGamblingSessionActionDataReceivedFromPlatform | IGamblingSessionActionDataReceivedFromPlatform[];

          try {
            actionsReceivedFromPlatform = JSON.parse(gamblingSessionActionEvent.MessageDataJson);
          } catch (err) {
            this.logger.error(`Failed to parse gambling session event MessageDataJson: ${gamblingSessionActionEvent.MessageDataJson}`, err);
            return;
          }

          if(!Check.isArray(actionsReceivedFromPlatform)) {
            actionsReceivedFromPlatform = [actionsReceivedFromPlatform];
          }

          for(let dataFromPlatform of actionsReceivedFromPlatform) {
            const dataToSend: IGamblingSessionActionData = {
              messageType: dataFromPlatform.MessageType,
              gamblingSessionId: dataFromPlatform.SessionId,
              sessionDurationInSeconds: dataFromPlatform.SessionDurationInSeconds,
              sessionTotalWins: dataFromPlatform.SessionTotalWins,
              sessionTotalBets: dataFromPlatform.SessionTotalBets,
              messageTimeStamp: dataFromPlatform.MessageTimeStamp,
              correlationID: dataFromPlatform.CorrelationID
            }
            Event.fire(SYSTEM_EVENTS.GAMBLING_SESSION_ACTION, dataToSend);
          }
          break;


      }
    });

    if (category){
      this.serviceFactory.firebaseElasticService().sendMessageUpdate(category, JSON.stringify(messages))
    }
  };

  private _updateRestrictions(data: IMessageData, restrictions: IRestrictionData[]) {
    let restrictionsByActions = [];
    restrictions.forEach((restriction) => {
      restriction.RestrictionsByAction.forEach((restrictionByAction)=> {
        restrictionsByActions.push(restrictionByAction);
      })
    });

    Event.fire(SYSTEM_EVENTS.SESSION_EVENT_UPDATERESTRICTION_RECEIVED, restrictionsByActions);
    
    //fire SYSTEM_EVENTS.GEO_RESTRICTIONS_UPDATE must happen after SYSTEM_EVENTS.SESSION_EVENT_UPDATERESTRICTION_RECEIVED
    Event.fire(SYSTEM_EVENTS.GEO_RESTRICTIONS_UPDATE, {KickReasonId: data?.ReasonID?.toString(), DetailedMessages: data?.TroubleshooterMessages, TroubleshooterRetry: Boolean(data?.TroubleshooterRetry)});


  }

  private _handleIntrusiveMessages(dataJsonArray: IntrusiveMessageData[]): void {
    //Seems like for this type of message we'll also get an array, like we do for toasters.
    if (!Check.isArray(dataJsonArray)) {
      dataJsonArray = [dataJsonArray];
    }

    dataJsonArray.forEach(dataJson => {
      this.serviceFactory.orbitComponentRenderService().showIntrusiveMessagePopup({
        title: dataJson.Title,
        text: dataJson.Text,
        buttonText: dataJson.ButtonText,
        correlationId: dataJson.CorrelationID
      });
    });
  }

  private _handleToasterTypeMessages(dataJsonArray: IMessageDataJson[]): void {

    if (!Check.isArray(dataJsonArray)) {
      dataJsonArray = [dataJsonArray];
    }

    const playerBurnoutMessages = [];
    const regularToasterMessages = [];

    dataJsonArray.forEach(dj => {
      if (this._isPlayerBurnoutMessage(dj)) {
        playerBurnoutMessages.push(dj);
      } else {
        regularToasterMessages.push(dj);
      }
    });

    //This comes as an array from Firebase, but in the burnout scenario
    // (where we can show only one animation at a given time),
    // I think it's correct to only consider the 1st one in the array
    if(playerBurnoutMessages.length > 0) {
      Event.fire(SYSTEM_EVENTS.SESSION_EVENT_PLAYER_BURNOUT_MESSAGE_RECEIVED, playerBurnoutMessages[0]);
    }

    if(regularToasterMessages.length > 0) {
      Event.fire(SYSTEM_EVENTS.SESSION_EVENT_RECEIVED_TOASTER, regularToasterMessages);
    }
  }

  private _isPlayerBurnoutMessage(dataJson: IMessageDataJson): boolean {
    return (dataJson.DisplayMode === MESSAGE_DISPLAY_MODE.ANIMATION &&
        dataJson.AnimationType === MESSAGE_ANIMATION_TYPE.BURNOUT)
  }

  private parseOmgMessage(data: IOMGReceivedData): IOMGParsedData {
    const omg: IOMGParsedData = {
      MessageDataJson: data.MessageDataJson,
      Messages: data.MessageData,
      OmgType: data.OmgType.toString(),
      OpenedWithinGame: data.DisplayInGame.toString() as OpenWithinGameType,
    };

    return omg;
  }

  private OMGFieldsValueChange(parsedMessage: IOMGParsedData) {
    if (!parsedMessage.MessageDataJson) {
      parsedMessage.OpenedWithinGame = '0';
      this.logger.debug('Doesnt get MessageDataJson field meaning that OMG is waiting messages type, OpenedWithinGame field is false');
    }
  }
}
